/**
 * MemorialDesignShowcase — strip of memorial-card artwork shown on the
 * Obituaries pages, inviting families to create an obituary and turn it
 * into a shareable memorial card page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpPublic } from "@/lib/api";
import { Feather, Palette, ArrowRight } from "lucide-react";

export default function MemorialDesignShowcase() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    httpPublic.get("/memorial-templates")
      .then((r) => setTemplates((r.data.templates || []).filter((t) => t.image_url)))
      .catch(() => setTemplates([]));
  }, []);
  if (templates.length === 0) return null;
  const shown = templates.slice(0, 6);
  return (
    <section className="mt-12 rounded-2xl border p-6 sm:p-8"
      style={{ borderColor: "rgba(91,74,115,.2)", background: "linear-gradient(135deg,#FAF8FF 0%,#FFF7ED 100%)" }}
      data-testid="memorial-design-showcase">
      <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="overline mb-2 flex items-center gap-2" style={{ color: "#5B4A73" }}>
            <Palette className="w-4 h-4" /> Memorial Card Designs
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">
            {templates.length + 1} handcrafted designs for a beautiful farewell
          </h2>
          <p className="mt-2 text-sm md:text-base max-w-xl" style={{ color: "var(--wofy-text-soft)" }}>
            Traditional · Floral · Regal — create an obituary, place their photo on the
            card you love, and share the memorial page on WhatsApp, or download it
            as a printable card.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/obituaries/submit" className="wofy-btn-primary inline-flex items-center gap-2"
              data-testid="showcase-cta-create">
              <Feather className="w-4 h-4" /> Create an Obituary Card <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto lg:overflow-visible pb-1" data-testid="showcase-thumbnails">
          {shown.map((t) => (
            <img key={t.key} src={t.image_url} alt={t.name} title={t.name} loading="lazy"
              className="h-36 sm:h-44 rounded-lg shadow-md object-cover shrink-0 transition-transform hover:scale-105 hover:-rotate-1"
              style={{ aspectRatio: t.aspect ? `${t.aspect[0]} / ${t.aspect[1]}` : "0.73" }} />
          ))}
        </div>
      </div>
    </section>
  );
}
